import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardMedia, CardContent,
  Button, Chip, Skeleton, Container, IconButton, useMediaQuery, useTheme
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DiamondIcon from '@mui/icons-material/Diamond';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import SecurityIcon from '@mui/icons-material/Security';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import useBehaviorTracking from '../hooks/useBehaviorTracking';
import { normalizeImageUrl, onImageErrorSetFallback } from '../utils/imageFallback';

const fixImageUrl = (url) => {
  return normalizeImageUrl(url, 'Product');
};

// Gemini 2.5 Pro - Fashion color palette
const C = {
  primary: '#c9a84c',
  primaryLight: '#dcb96b',
  primaryDark: '#a78a3e',
  black: '#1a1a1a',
  darkGray: '#2d2d2d',
  text: '#1a1a1a',
  textSecondary: '#5b5b5b',
  textDisabled: '#a9a9a9',
  white: '#ffffff',
  cream: '#faf9f6',
  hoverBg: 'rgba(201, 168, 76, 0.08)',
};

const TemplateFashion = () => {
  const [featured, setFeatured] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [hotProducts, setHotProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBanner, setCurrentBanner] = useState(0);
  const { addToCart } = useCart();
  const { storeConfig, shippingConfig, categories: dbCategories } = useStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation('common');
  useBehaviorTracking();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [featuredRes, newRes, hotRes] = await Promise.all([
          api.get('/products/featured?limit=8'),
          api.get('/products?sort=newest&limit=8'),
          api.get('/products?sort=sales&limit=8')
        ]);
        setFeatured(featuredRes.data || []);
        setNewProducts(newRes.data.items || []);
        setHotProducts(hotRes.data.items || []);
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const banners = [
    {
      title: storeConfig.store_name || 'FASHION',
      subtitle: storeConfig.slogan || 'Timeless Elegance',
      bgcolor: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
      bgImage: '/images/banners/banner-fashion.jpg',
    },
    {
      title: 'NEW ARRIVALS',
      subtitle: t('template.new_arrival_limited'),
      bgcolor: `linear-gradient(160deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
    },
    {
      title: 'FREE SHIPPING',
      subtitle: t('template.free_shipping_fee', { threshold: shippingConfig?.free_shipping_threshold || 799 }),
      bgcolor: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => setCurrentBanner(p => (p + 1) % banners.length), 6000);
    return () => clearInterval(timer);
  }, [banners.length]);

  // Gemini: 3:4 aspect ratio, no border-radius, hover translateY(-8px) + scale image
  const ProductCard = ({ product }) => (
    <Card sx={{
      height: '100%', display: 'flex', flexDirection: 'column',
      bgcolor: C.white, borderRadius: 0, overflow: 'hidden',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: 'none',
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0px 10px 20px rgba(0,0,0,0.08)',
        '& .product-img': { transform: 'scale(1.05)' },
      }
    }}>
      <Box sx={{ position: 'relative', pt: '133%', bgcolor: C.cream, cursor: 'pointer', overflow: 'hidden' }} onClick={() => navigate(`/products/${product.id}`)}>
        <CardMedia className="product-img" component="img" image={fixImageUrl(product.image_url)} alt={product.name} loading="lazy"
          onError={(e) => onImageErrorSetFallback(e, product.name)}
          sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' }} />
        {product.quantity <= 5 && product.quantity > 0 && (
          <Chip label="LIMITED" size="small" sx={{ position: 'absolute', top: 12, left: 12, bgcolor: C.black, color: C.primary, fontWeight: 600, fontSize: '0.6rem', letterSpacing: '0.15em', height: 22, borderRadius: 0 }} />
        )}
        {product.original_price > product.sale_price && (
          <Chip label="SALE" size="small" sx={{ position: 'absolute', top: 12, right: 12, bgcolor: C.primary, color: C.black, fontWeight: 700, fontSize: '0.6rem', height: 22, borderRadius: 0 }} />
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1, p: 2.5, textAlign: 'center' }}>
        <Typography variant="body2" sx={{
          overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: 44,
          color: C.text, fontWeight: 400, letterSpacing: '0.02em', lineHeight: 1.6,
          cursor: 'pointer', transition: 'color 0.3s',
          '&:hover': { color: C.primary }
        }} onClick={() => navigate(`/products/${product.id}`)}>{product.name}</Typography>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 1.5 }}>
          <Typography sx={{ color: C.primary, fontWeight: 'bold', fontSize: '1.1rem', letterSpacing: '0.03em' }}>
            NT$ {product.sale_price ? Math.round(Number(product.sale_price)).toLocaleString() : 0}
          </Typography>
          {product.original_price > product.sale_price && (
            <Typography variant="caption" sx={{ color: C.textDisabled, textDecoration: 'line-through' }}>
              NT$ {product.original_price ? Math.round(Number(product.original_price)).toLocaleString() : 0}
            </Typography>
          )}
        </Box>
      </CardContent>
      <Box sx={{ px: 2.5, pb: 2.5 }}>
        <Button fullWidth variant="outlined" startIcon={<ShoppingCartIcon sx={{ fontSize: 16 }} />}
          onClick={() => addToCart(product.id)} disabled={product.quantity <= 0}
          sx={{
            borderColor: C.black, color: C.black, borderRadius: 0,
            textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.1em',
            fontSize: '0.7rem', py: 1.2,
            transition: 'all 0.3s ease',
            '&:hover': { bgcolor: C.black, color: C.white, borderColor: C.black },
            '&.Mui-disabled': { borderColor: C.textDisabled, color: C.textDisabled }
          }}>
          {product.quantity > 0 ? t('template.add_to_bag') : t('template.sold_out_en')}
        </Button>
      </Box>
    </Card>
  );

  // Gemini: section headers with gold line + overline
  const SectionTitle = ({ overline, title }) => (
    <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
      <Typography variant="overline" sx={{ color: C.primary, letterSpacing: '0.15em', fontSize: '0.75rem' }}>{overline}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 300, mt: 0.5, letterSpacing: '0.08em', color: C.text, fontSize: { xs: '1.6rem', md: '2.2rem' } }}>{title}</Typography>
      <Box sx={{ width: 50, height: 1, bgcolor: C.primary, mx: 'auto', mt: 2 }} />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* Gemini: Category nav with underline animation */}
      <Box sx={{ bgcolor: C.black, py: 1.2, overflowX: 'auto', '&::-webkit-scrollbar': { height: 0 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: { xs: 3, md: 5 }, justifyContent: 'center', minWidth: 'max-content' }}>
            <Typography variant="caption" onClick={() => navigate('/products')}
              sx={{
                color: C.primaryLight, cursor: 'pointer', letterSpacing: '0.15em', fontWeight: 600,
                textTransform: 'uppercase', position: 'relative', pb: 0.5,
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, width: '100%', height: '1px', bgcolor: C.primary, transform: 'scaleX(1)' },
              }}>ALL</Typography>
            {dbCategories.slice(0, 6).map(cat => (
              <Typography key={cat.name} variant="caption"
                onClick={() => navigate(`/products?category=${encodeURIComponent(cat.name)}`)}
                sx={{
                  color: 'rgba(255,255,255,0.65)', cursor: 'pointer', letterSpacing: '0.1em',
                  textTransform: 'uppercase', position: 'relative', pb: 0.5, transition: 'color 0.3s',
                  '&::after': { content: '""', position: 'absolute', bottom: 0, left: '50%', width: '100%', height: '1px', bgcolor: C.primary, transform: 'translateX(-50%) scaleX(0)', transition: 'transform 0.3s ease' },
                  '&:hover': { color: C.primary, '&::after': { transform: 'translateX(-50%) scaleX(1)' } }
                }}>{cat.name}</Typography>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Gemini: 95vh Hero with staggered animation */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)', transform: `translateX(-${currentBanner * 100}%)` }}>
          {banners.map((banner, idx) => (
            <Box key={idx} sx={{
              minWidth: '100%', background: banner.bgcolor,
              backgroundImage: banner.bgImage ? `url(${banner.bgImage})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              minHeight: { xs: 220, md: 360 },
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              position: 'relative', px: 3,
            }}>
              <Box sx={{ width: 40, height: 1, bgcolor: C.primary, mb: 2, animation: currentBanner === idx ? 'fadeIn 0.8s ease 0s forwards' : 'none', opacity: currentBanner === idx ? 1 : 0 }} />
              <Typography sx={{
                color: C.white, fontWeight: 300,
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3rem' },
                letterSpacing: { xs: '0.05em', md: '0.1em' },
                textAlign: 'center', textTransform: 'uppercase', lineHeight: 1.1,
                animation: currentBanner === idx ? 'slideUp 0.8s ease 0.2s forwards' : 'none',
                opacity: currentBanner === idx ? 1 : 0,
              }}>{banner.title}</Typography>
              <Typography variant="body2" sx={{
                color: C.primaryLight, mt: 1.5, letterSpacing: '0.2em', textAlign: 'center',
                fontSize: { xs: '0.8rem', md: '0.9rem' },
                animation: currentBanner === idx ? 'slideUp 0.8s ease 0.4s forwards' : 'none',
                opacity: currentBanner === idx ? 1 : 0,
              }}>{banner.subtitle}</Typography>
              <Button variant="outlined" onClick={() => navigate('/products')}
                sx={{
                  mt: 3, borderColor: C.primary, color: C.primary, borderRadius: 0,
                  px: { xs: 3, md: 5 }, py: 1, letterSpacing: '0.15em', fontWeight: 400,
                  fontSize: '0.75rem',
                  transition: 'all 0.4s ease',
                  animation: currentBanner === idx ? 'slideUp 0.8s ease 0.6s forwards' : 'none',
                  opacity: currentBanner === idx ? 1 : 0,
                  '&:hover': { bgcolor: C.primary, color: C.black, borderColor: C.primary }
                }}>SHOP NOW</Button>
            </Box>
          ))}
        </Box>
        <IconButton onClick={() => setCurrentBanner(p => (p - 1 + banners.length) % banners.length)}
          sx={{ position: 'absolute', left: { xs: 8, md: 24 }, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.6)', '&:hover': { color: C.white } }}>
          <ArrowBackIosNewIcon />
        </IconButton>
        <IconButton onClick={() => setCurrentBanner(p => (p + 1) % banners.length)}
          sx={{ position: 'absolute', right: { xs: 8, md: 24 }, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.6)', '&:hover': { color: C.white } }}>
          <ArrowForwardIosIcon />
        </IconButton>
        {/* Dots */}
        <Box sx={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1.5 }}>
          {banners.map((_, i) => (
            <Box key={i} onClick={() => setCurrentBanner(i)}
              sx={{ width: currentBanner === i ? 24 : 8, height: 3, bgcolor: currentBanner === i ? C.primary : 'rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'all 0.4s ease', borderRadius: 0 }} />
          ))}
        </Box>
      </Box>

      {/* Gemini: Shipping strip */}
      <Box sx={{ bgcolor: C.black, py: 1.8, borderBottom: `1px solid ${C.darkGray}` }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {[
              { icon: <LocalShippingIcon sx={{ fontSize: 16 }} />, text: t('template.free_shipping_threshold', { threshold: shippingConfig?.free_shipping_threshold || 799 }) },
              { icon: <VerifiedIcon sx={{ fontSize: 16 }} />, text: t('template.authentic_guarantee') },
              { icon: <SecurityIcon sx={{ fontSize: 16 }} />, text: t('template.safe_payment_label') },
              { icon: <DiamondIcon sx={{ fontSize: 16 }} />, text: t('template.exquisite_packaging') },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: C.primaryLight }}>
                  {item.icon}
                  <Typography variant="caption" sx={{ letterSpacing: '0.08em', fontSize: '0.7rem' }}>{item.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Gemini: spacing py: { xs: 8, md: 12 } between sections */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        {/* Featured */}
        <SectionTitle overline="CURATED FOR YOU" title={t('template.curated_for_you')} />
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {loading ? Array(8).fill(0).map((_, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <Skeleton variant="rectangular" sx={{ pt: '133%', borderRadius: 0 }} />
              <Skeleton width="60%" sx={{ mt: 1.5, mx: 'auto' }} />
              <Skeleton width="40%" sx={{ mx: 'auto' }} />
            </Grid>
          )) : featured.map(product => (
            <Grid item xs={6} sm={4} md={3} key={product.id}><ProductCard product={product} /></Grid>
          ))}
        </Grid>

        {/* Lookbook / brand story section - Gemini unique */}
        <Box sx={{ mt: { xs: 8, md: 12 }, mb: { xs: 8, md: 12 } }}>
          <Grid container spacing={0}>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: C.black, p: { xs: 4, md: 8 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="overline" sx={{ color: C.primary, letterSpacing: '0.2em', mb: 2 }}>OUR STORY</Typography>
                <Typography variant="h4" sx={{ color: C.white, fontWeight: 300, letterSpacing: '0.05em', lineHeight: 1.4, mb: 3, fontSize: { xs: '1.4rem', md: '2rem' } }}>
                  {storeConfig.description || t('template.brand_story_default')}
                </Typography>
                <Button variant="outlined" onClick={() => navigate('/products')}
                  sx={{ alignSelf: 'flex-start', borderColor: C.primary, color: C.primary, borderRadius: 0, letterSpacing: '0.1em', px: 4, '&:hover': { bgcolor: C.primary, color: C.black } }}>
                  EXPLORE
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: C.cream, minHeight: { xs: 250, md: 400 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ color: C.textDisabled, letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.85rem' }}>LOOKBOOK</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Hot */}
        {hotProducts.length > 0 && (
          <>
            <SectionTitle overline="BEST SELLERS" title={t('template.best_sellers')} />
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
              {hotProducts.slice(0, 4).map(product => (
                <Grid item xs={6} sm={4} md={3} key={product.id}><ProductCard product={product} /></Grid>
              ))}
            </Grid>
          </>
        )}

        {/* New arrivals */}
        {newProducts.length > 0 && (
          <Box sx={{ mt: { xs: 8, md: 12 } }}>
            <SectionTitle overline="NEW ARRIVALS" title={t('template.new_arrivals_title')} />
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
              {newProducts.slice(0, 4).map(product => (
                <Grid item xs={6} sm={4} md={3} key={`new-${product.id}`}><ProductCard product={product} /></Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Brand guarantee - Gemini: bottom section */}
        <Box sx={{ mt: { xs: 8, md: 12 }, p: { xs: 4, md: 8 }, bgcolor: C.black, textAlign: 'center' }}>
          <Typography variant="overline" sx={{ color: C.primary, letterSpacing: '0.2em', display: 'block', mb: 4, fontSize: '0.75rem' }}>WHY CHOOSE US</Typography>
          <Grid container spacing={4}>
            {[
              { icon: <DiamondIcon />, title: t('template.selected_quality'), desc: t('template.selected_quality_desc') },
              { icon: <LocalShippingIcon />, title: t('template.fast_arrival'), desc: t('template.fast_arrival_desc') },
              { icon: <SecurityIcon />, title: t('template.safe_transaction'), desc: t('template.safe_transaction_desc') },
              { icon: <StarIcon />, title: t('template.caring_service'), desc: storeConfig.service_hours || t('template.service_hours_short') },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Box sx={{ color: C.primary, mb: 1.5, '& svg': { fontSize: 32 } }}>{item.icon}</Box>
                <Typography variant="subtitle2" sx={{ color: C.white, fontWeight: 600, letterSpacing: '0.05em', mb: 0.5 }}>{item.title}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: '0.03em' }}>{item.desc}</Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {/* CSS Animations */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

export default TemplateFashion;


